import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

// How long to wait for Rebuy to initialise before falling back to the native drawer (ms).
const REBUY_TIMEOUT = 1000;
const REBUY_POLL_INTERVAL = 100;

/**
 * Attempts to open the Rebuy Smart Cart.
 * If Rebuy is ready, opens it immediately and returns true.
 * If Rebuy is still loading, polls until it is ready or the timeout is reached.
 * Returns a Promise that resolves to true if Rebuy opened, false if we should fall back.
 *
 * @returns {Promise<boolean>}
 */
function tryOpenRebuy() {
  return new Promise((resolve) => {
    // Rebuy already initialised — open straight away.
    if (typeof window.Rebuy !== 'undefined' && window.Rebuy.SmartCart?.status === 'ready') {
      window.Rebuy.SmartCart.show();
      resolve(true);
      return;
    }

    // Rebuy exists but may still be booting — wait a tick.
    if (typeof window.Rebuy !== 'undefined' && window.Rebuy.SmartCart) {
      let elapsed = 0;
      const interval = setInterval(() => {
        elapsed += REBUY_POLL_INTERVAL;

        if (window.Rebuy.SmartCart?.status === 'ready') {
          clearInterval(interval);
          window.Rebuy.SmartCart.show();
          resolve(true);
          return;
        }

        if (elapsed >= REBUY_TIMEOUT) {
          clearInterval(interval);
          resolve(false); // Fall back to native drawer.
        }
      }, REBUY_POLL_INTERVAL);
      return;
    }

    // Rebuy is not present at all — fall back immediately.
    resolve(false);
  });
}

/**
 * A custom element that manages a cart drawer.
 *
 * Opens the Rebuy Smart Cart when available; falls back to the native
 * drawer if Rebuy is not installed or not yet initialised.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * Called when a product is added to the cart.
   * Respects the auto-open attribute and prefers Rebuy when available.
   */
  #handleCartAdd = async () => {
    if (!this.hasAttribute('auto-open')) return;

    const rebuyOpened = await tryOpenRebuy();
    if (!rebuyOpened) {
      // Rebuy not found — open the native drawer.
      this.#openNative();
    }
  };

  /**
   * Called by the on:click="/open" declarative handler on the cart button.
   * Tries Rebuy first; falls back to the native drawer.
   */
  async open() {
    const rebuyOpened = await tryOpenRebuy();
    if (!rebuyOpened) {
      this.#openNative();
    }
  }

  /**
   * Opens the native cart drawer and wires up the installments CTA close behaviour.
   */
  #openNative() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}


function initCartUpsellSlider() {
  new Swiper('[js-cart-upsell-slider]', {
    slidesPerView: 2.2,
    spaceBetween: 18,
    loop: false,
    freeMode: true,
    mousewheel: {
      forceToAxis: true, 
      sensitivity: 1, 
    },
    breakpoints: {
      0: {
        slidesPerView: 1.76,
          spaceBetween: 13,
      },
      768: {
        slidesPerView: 2.26,
        spaceBetween: 18,
      }
    }
  });
}

initCartUpsellSlider();
document.addEventListener("cart-upsell-slider:init", initCartUpsellSlider);